import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../login/AuthContext";
import { useNavigate } from "react-router-dom";
import { AddCircleOutline } from "@mui/icons-material";
import axios from "axios";
import config from "../../config/config.js";
import { StatusCellRenderer } from "../../components/DataTable/StatusCellRenderer.jsx";
import { dateFormatter } from "../../components/DataTable/dateFormatter.js";
import IconActionsRenderer from "../../components/IconActionsRenderer.jsx";
import { toast } from "react-toastify";

const LicensePage = () => {
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  const dataTableRef = useRef(null);
  const [aktifPasif, setAktifPasif] = useState("aktif");
  const isBayiFullAccess = Number(userData?.BayiId ?? userData?.BayiKodu ?? 0) === 1;
  const customQueryParams = useMemo(() => ({ aktifPasif }), [aktifPasif]);

  useEffect(() => {
    if (!dataTableRef.current?.refreshData) return;
    const t = setTimeout(() => dataTableRef.current.refreshData(), 0);
    return () => clearTimeout(t);
  }, [aktifPasif]);

  // Navigate to license request page
  const toggleDrawer = () => navigate("/lisanslar/lisans-talep");

  // Lisans talebi silme (aktif listeden pasife al; pasif listeden Bayi 1 kalıcı siler) – sadece Bayi 1
  const handleDeleteLicense = useCallback(
    async (id) => {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
      return api.put(`/lisanslar/lisans-sil/${id}`, {}, { params: { aktifPasif } });
    },
    [token, aktifPasif]
  );

  // Lisans talebini pasif listeden aktife al (sadece Bayi 1)
  const handleAktifeAl = useCallback(
    async (id) => {
      try {
        const api = axios.create({
          baseURL: config.baseURL,
          headers: { Authorization: `Bearer ${token}` },
        });
        await api.put(`/lisanslar/lisans-aktife-al/${id}`);
        toast.success("Lisans talebi aktife alındı.");
        if (dataTableRef.current?.refreshData) dataTableRef.current.refreshData();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Lisans talebi aktife alınamadı.");
      }
    },
    [token]
  );

  const licenseCustomContext = useMemo(
    () => ({
      isLicenseTable: true,
      showDelete: isBayiFullAccess,
      showRestore: isBayiFullAccess && aktifPasif === "pasif",
      onRestore: handleAktifeAl,
    }),
    [isBayiFullAccess, aktifPasif, handleAktifeAl]
  );

  // Debounce ile searchText güncelleme
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      if (value.length >= 3 && dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      } else if (value.length < 3 && dataTableRef.current?.refreshData) {
        // 3 karakterden az girildiyse tabloyu resetleyebiliriz
        dataTableRef.current.refreshData();
      }
    }, 300);
  };

  const handleForwardLicense = async (licenseId) => {
    try {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` }
      });
      return await api.put(`/lisanslar/lisanstalep/yonlendir/${licenseId}`);
    } catch (error) {
      console.error('Lisans talebi yönlendirme hatası:', error.response?.data);
      throw error;
    }
  };

  // Varsayılan sütun tanımları (LicenseTable)
  const defaultLicenseColumnDefs = useMemo(() => [
    {
      headerName: 'İşlemler',
      field: 'actions',
      width: 110,
      maxWidth: 130,
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: IconActionsRenderer,
      pinned: 'left',
      cellClass: 'action-cell-center'
    },
    {
      headerName: 'Bayi',
      field: 'BayiAdi',
      sortable: true,
      filter: true,
      width: 120,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Yönlendirilen Bayi',
      field: 'YonlendirilenBayiAdi',
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.data ? params.value || 'Yönlendirilmedi' : '',
      width: 150,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Lisans No',
      field: 'LisansNo',
      sortable: true,
      filter: true,
      width: 120,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Lisans Türü',
      field: 'LisansTipi',
      sortable: true,
      filter: true,
      width: 120,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Müşteri',
      field: 'Musteri.Unvan',
      valueGetter: params => params.data?.Musteri?.Unvan || '',
      sortable: true,
      filter: true,
      width: 200,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Başlangıç Tarihi',
      field: 'BaslangicTarih',
      valueFormatter: dateFormatter,
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 140,
      filterParams: {
        buttons: ['sıfırla', 'uygula'], closeOnApply: true,
        comparator: (filterDate, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          return cellDate < filterDate ? -1 : cellDate > filterDate ? 1 : 0;
        },
        browserDatePicker: true,
        minValidYear: 2020,
        maxValidYear: 2050,
        inRangeFloatingFilterDateFormat: 'DD.MM.YYYY'
      }
    },
    {
      headerName: 'Bitiş Tarihi',
      field: 'BitisTarih',
      valueFormatter: dateFormatter,
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 140,
      filterParams: {
        buttons: ['sıfırla', 'uygula'], closeOnApply: true,
        comparator: (filterDate, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          return cellDate < filterDate ? -1 : cellDate > filterDate ? 1 : 0;
        },
        browserDatePicker: true,
        minValidYear: 2020,
        maxValidYear: 2050,
        inRangeFloatingFilterDateFormat: 'DD.MM.YYYY'
      }
    },
    {
      headerName: 'Açıklama',
      field: 'Aciklama',
      sortable: true,
      filter: true,
      width: 200,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    },
    {
      headerName: 'Durum',
      field: 'DurumAd',
      sortable: true,
      filter: true,
      width: 140,
      cellRenderer: StatusCellRenderer,
      filterParams: { buttons: ['sıfırla', 'uygula'], closeOnApply: true }
    }
  ], []);

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Lisans Yenileme Talepleri</h1>
      </div>

      {isBayiFullAccess && (
        <FormControl component="fieldset" sx={{ mb: 1.5, display: "block" }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 0.5 }}>
            Liste
          </FormLabel>
          <RadioGroup
            row
            value={aktifPasif}
            onChange={(e) => setAktifPasif(e.target.value)}
            name="aktif-pasif-lisans"
          >
            <FormControlLabel value="aktif" control={<Radio size="small" />} label="Aktif" />
            <FormControlLabel value="pasif" control={<Radio size="small" />} label="Pasifler" />
          </RadioGroup>
        </FormControl>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama (En az 3 karakter)"
          value={searchText}
          onChange={handleSearchChange}
          className="search-input"
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={toggleDrawer}
          sx={{ ml: 2 }}
          size="medium"
        >
          Ekle
        </Button>
      </div>

      <DataTable
        ref={dataTableRef}
        searchText={searchText}
        tableType="licenses"
        columnDefs={defaultLicenseColumnDefs}
        apiEndpoint="/lisanslar/lisanstalepleri"
        searchEndpoint="/lisanslar/search"
        editPath="/lisanslar/lisans-talep"
        onRowDoubleClicked={(e) => { const id = e.data?.Id; if (id != null) navigate(`/lisanslar/lisans-talep/${id}`); }}
        customQueryParams={customQueryParams}
        customContext={licenseCustomContext}
        onForwardAction={handleForwardLicense}
        onDeleteAction={handleDeleteLicense}
        forwardConfirmMessage="Bu lisans yenileme talebini üst bayiye yönlendirmek istediğinizden emin misiniz?"
        deleteConfirmMessage={aktifPasif === "pasif" ? "Bu lisans talebini kalıcı olarak silmek istediğinizden emin misiniz?" : "Bu lisans talebini pasife almak (silinen listesine taşımak) istediğinizden emin misiniz?"}
      />
    </div>
  );
};

export default LicensePage;
